import React, { useState, useEffect, useCallback } from 'react';
import { apiCall } from '../config';
import { notify, confirmDialog } from '../ui/notify';

// Tischreservierungen Wiesn Garten (Formular auf wiesn-garten.de). Sichtbar für
// Leitung, Einsatzleitung, WG-Planer und alle für den Wiesn-Garten Freigeschalteten.
// Max. 4 Tische gleichzeitig, 1 Tisch = 10 Personen, Dauer 2,5 Std – geprüft im Backend.
const berlinToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin' }).format(new Date());
const SEASON = { start: '2026-09-19', end: '2026-10-04' };
const WD = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const dateDE = (iso) => `${WD[new Date(iso + 'T12:00:00').getDay()]}, ${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;
const shift = (iso, d) => { const x = new Date(iso + 'T12:00:00'); x.setDate(x.getDate() + d); return x.toISOString().slice(0, 10); };
const toMin = (t) => (t === '24:00' ? 1440 : Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5)));

const STATUS = {
  active: { label: 'reserviert', cls: 'bg-primary' },
  arrived: { label: 'da', cls: 'bg-success' },
  noshow: { label: 'nicht erschienen', cls: 'bg-warning text-dark' },
  cancelled: { label: 'storniert', cls: 'bg-secondary' },
};

const WgReservations = () => {
  const start = berlinToday() < SEASON.start ? SEASON.start : (berlinToday() > SEASON.end ? SEASON.end : berlinToday());
  const [day, setDay] = useState(start);
  const [data, setData] = useState({ tables: 4, seats: 10, reservations: [] });
  const [loading, setLoading] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  const [form, setForm] = useState({ time: '18:00', persons: 4, name: '', phone: '', note: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await apiCall(`/wg/reservations?from=${day}&to=${day}`)); }
    catch (e) { notify('Reservierungen konnten nicht geladen werden: ' + e.message); }
    setLoading(false);
  }, [day]);
  useEffect(() => { load(); }, [load]);
  // Alle 60 Sekunden aktualisieren – Reservierungen kommen live von der Website
  useEffect(() => { const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const setStatus = async (r, status) => {
    if (status === 'cancelled') {
      const ok = await confirmDialog(`Reservierung #${r.id} von ${r.name} stornieren?${r.email ? ' Der Gast bekommt eine E-Mail.' : ''}`);
      if (!ok) return;
    }
    try {
      await apiCall(`/wg/reservations/${r.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }) });
      load();
    } catch (e) { notify('Fehler: ' + e.message); }
  };

  const addManual = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await apiCall('/wg/reservations', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: day, ...form }),
      });
      setForm({ ...form, name: '', phone: '', note: '' });
      load();
    } catch (err) { notify(err.message || 'Eintragen fehlgeschlagen'); }
    setBusy(false);
  };

  const live = data.reservations.filter((r) => r.status === 'active' || r.status === 'arrived');
  const shown = data.reservations.filter((r) => showCancelled || r.status !== 'cancelled');
  // Belegung je halbe Stunde 10:00–24:00
  const slots = [];
  for (let m = 600; m < 1440; m += 30) {
    const used = live.reduce((s, r) => (toMin(r.start_time) <= m && toMin(r.end_time) > m ? s + (r.tables_count || 1) : s), 0);
    slots.push({ m, used });
  }
  const times = []; for (let m = 600; m <= 21 * 60 + 30; m += 30) times.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);

  return (
    <div className="container-fluid py-3">
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <h4 className="mb-0 me-auto">🍺 Tischreservierungen Wiesn Garten</h4>
        <button className="btn btn-outline-secondary btn-sm" onClick={() => setDay(shift(day, -1))} disabled={day <= SEASON.start}>←</button>
        <input type="date" className="form-control form-control-sm" style={{ width: 160 }} value={day} min={SEASON.start} max={SEASON.end} onChange={(e) => e.target.value && setDay(e.target.value)} />
        <button className="btn btn-outline-secondary btn-sm" onClick={() => setDay(shift(day, 1))} disabled={day >= SEASON.end}>→</button>
        <button className="btn btn-outline-primary btn-sm" onClick={() => setDay(start)}>Heute</button>
      </div>

      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="small text-muted mb-1">{dateDE(day)} · Belegung ({data.tables} Tische reservierbar, 1 Tisch = {data.seats} Personen)</div>
          <div className="d-flex" style={{ gap: 2 }}>
            {slots.map((s) => (
              <div key={s.m} title={`${String(Math.floor(s.m / 60)).padStart(2, '0')}:${String(s.m % 60).padStart(2, '0')} – ${s.used}/${data.tables} Tische`}
                style={{ flex: 1, height: 22, borderRadius: 3, background: s.used === 0 ? '#e9ecef' : s.used >= data.tables ? '#dc3545' : s.used >= data.tables - 1 ? '#fd7e14' : '#198754', opacity: s.used === 0 ? 1 : 0.85 }} />
            ))}
          </div>
          <div className="d-flex justify-content-between small text-muted mt-1"><span>10:00</span><span>14:00</span><span>18:00</span><span>22:00</span><span>24:00</span></div>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-header d-flex justify-content-between align-items-center">
          <strong>{live.length} Reservierung{live.length === 1 ? '' : 'en'} · {live.reduce((s, r) => s + r.persons, 0)} Personen</strong>
          <label className="small mb-0"><input type="checkbox" className="form-check-input me-1" checked={showCancelled} onChange={(e) => setShowCancelled(e.target.checked)} />Stornierte zeigen</label>
        </div>
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <thead className="table-light"><tr><th>Zeit</th><th>Name</th><th>Pers.</th><th>Telefon</th><th>Anmerkung</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {loading && !shown.length && <tr><td colSpan={7} className="text-center text-muted py-3">lädt …</td></tr>}
              {!loading && !shown.length && <tr><td colSpan={7} className="text-center text-muted py-3">Keine Reservierungen an diesem Tag.</td></tr>}
              {shown.map((r) => (
                <tr key={r.id} className={r.status === 'cancelled' ? 'text-muted' : ''}>
                  <td className="text-nowrap"><strong>{r.start_time}</strong>–{r.end_time}</td>
                  <td>{r.name} <span className="text-muted small">#{r.id}{r.source === 'manual' ? ' · manuell' : ''}</span></td>
                  <td>{r.persons}{r.tables_count > 1 ? ` (${r.tables_count} T.)` : ''}</td>
                  <td className="text-nowrap">{r.phone ? <a href={`tel:${r.phone}`}>{r.phone}</a> : '–'}</td>
                  <td className="small">{r.note || ''}</td>
                  <td><span className={`badge ${STATUS[r.status]?.cls || 'bg-secondary'}`}>{STATUS[r.status]?.label || r.status}</span></td>
                  <td className="text-nowrap text-end">
                    {r.status === 'active' && <>
                      <button className="btn btn-sm btn-outline-success me-1" onClick={() => setStatus(r, 'arrived')}>✓ da</button>
                      <button className="btn btn-sm btn-outline-warning me-1" onClick={() => setStatus(r, 'noshow')}>nicht da</button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => setStatus(r, 'cancelled')}>✗</button>
                    </>}
                    {(r.status === 'arrived' || r.status === 'noshow') && <button className="btn btn-sm btn-outline-secondary" onClick={() => setStatus(r, 'active')}>zurück</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><strong>Manuell eintragen</strong> <span className="text-muted small">(vor Ort oder am Telefon angenommen, für {dateDE(day)})</span></div>
        <form className="card-body row g-2 align-items-end" onSubmit={addManual}>
          <div className="col-6 col-md-2"><label className="form-label small mb-0">Uhrzeit</label>
            <select className="form-select form-select-sm" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}>{times.map((t) => <option key={t}>{t}</option>)}</select></div>
          <div className="col-6 col-md-1"><label className="form-label small mb-0">Pers.</label>
            <input type="number" min={1} max={40} className="form-control form-control-sm" value={form.persons} onChange={(e) => setForm({ ...form, persons: e.target.value })} /></div>
          <div className="col-12 col-md-3"><label className="form-label small mb-0">Name</label>
            <input className="form-control form-control-sm" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
          <div className="col-12 col-md-2"><label className="form-label small mb-0">Telefon</label>
            <input className="form-control form-control-sm" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <div className="col-12 col-md-3"><label className="form-label small mb-0">Anmerkung</label>
            <input className="form-control form-control-sm" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
          <div className="col-12 col-md-1"><button className="btn btn-primary btn-sm w-100" disabled={busy || !form.name.trim()}>＋</button></div>
        </form>
      </div>
    </div>
  );
};

export default WgReservations;
